import React, { ErrorInfo } from "react";
import { withScope, captureException } from "@sentry/react";

interface Props {
  children: React.ReactNode;
  /**
   * When this prop changes, the error state is reset.
   * Used to clear the error boundary when the user navigates to a different route.
   */
  resetKey?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidUpdate(prevProps: Props) {
    // Reset error state when resetKey changes (e.g., route navigation)
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: undefined });
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // US237: forward the error to Sentry with component stack trace
    withScope((scope) => {
      scope.setExtras({ componentStack: errorInfo.componentStack ?? null });
      captureException(error);
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      const isDev = process.env.NODE_ENV === "development";

      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-secondary-50 px-4">
          <div className="max-w-md w-full text-center">
            <h1 className="text-3xl font-bold text-primary-900 mb-4">
              Something went wrong
            </h1>
            <p className="text-secondary-700 mb-4">
              An unexpected error occurred. Please try one of the options below.
            </p>

            {isDev && this.state.error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-left">
                <p className="text-sm font-semibold text-red-800 mb-1">
                  Error details (development only):
                </p>
                <pre className="text-xs text-red-700 whitespace-pre-wrap break-all">
                  {this.state.error.message}
                  {"\n"}
                  {this.state.error.stack}
                </pre>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleReload}
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Reload Page
              </button>
              <button
                onClick={this.handleGoHome}
                className="inline-flex items-center justify-center px-6 py-3 border border-secondary-300 text-base font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Go to Home Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
